import React, {
    useRef,
    useState,
    useEffect,
    useMemo,
    useCallback,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import styles from "./UniversalReportTable.module.css";

type ExcelRow = Record<string, any>;

type UniversalReportProps = {
    data: ExcelRow[];
    columns: string[];
};

const ROW_HEIGHT = 44;
const DEFAULT_COL_WIDTH = 140;
const MIN_COL_WIDTH = 80;

const UniversalReportTable = ({ data, columns }: UniversalReportProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const resizingColRef = useRef<string | null>(null);
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);

    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

    useEffect(() => {
        setColumnWidths(prev =>
            columns.reduce(
                (acc, col) => ({
                    ...acc,
                    [col]: prev[col] ?? DEFAULT_COL_WIDTH,
                }),
                {} as Record<string, number>
            )
        );
    }, [columns]);

    const tableWidth = useMemo(
        () =>
            columns.reduce(
                (sum, col) => sum + (columnWidths[col] ?? DEFAULT_COL_WIDTH),
                0
            ),
        [columns, columnWidths]
    );

    const rowVirtualizer = useVirtualizer({
        count: data.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: 5,
    });

    const onMouseMove = useCallback((e: MouseEvent) => {
        if (!resizingColRef.current) return;

        const delta = e.clientX - startXRef.current;
        const nextWidth = Math.max(
            MIN_COL_WIDTH,
            startWidthRef.current + delta
        );

        setColumnWidths(prev => ({
            ...prev,
            [resizingColRef.current as string]: nextWidth,
        }));
    }, []);

    const onMouseUp = useCallback(() => {
        resizingColRef.current = null;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
    }, [onMouseMove]);

    const onResizeMouseDown = (
        e: React.MouseEvent,
        col: string
    ) => {
        e.preventDefault();
        e.stopPropagation();

        resizingColRef.current = col;
        startXRef.current = e.clientX;
        startWidthRef.current = columnWidths[col];

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    return (
        <div ref={scrollRef} className={styles.tableWrapper}>
            <div
                className={styles.header}
                style={{ width: tableWidth }}
            >
                {columns.map(col => (
                    <div
                        key={col}
                        className={styles.th}
                        style={{ width: columnWidths[col] }}
                    >
                        <div className={styles.thContent}>{col}</div>

                        <div
                            className={styles.resizer}
                            onMouseDown={e => onResizeMouseDown(e, col)}
                        />
                    </div>
                ))}
            </div>

            <div className={styles.body}>
                <div
                    style={{
                        height: rowVirtualizer.getTotalSize(),
                        width: tableWidth,
                        position: "relative",
                    }}
                >
                    {rowVirtualizer.getVirtualItems().map(virtualRow => {
                        const row = data[virtualRow.index];

                        return (
                            <div
                                key={virtualRow.key}
                                className={styles.tr}
                                style={{
                                    width: tableWidth,
                                    position: "absolute",
                                    transform: `translateY(${virtualRow.start}px)`,
                                }}
                            >
                                {columns.map(col => (
                                    <div
                                        key={col}
                                        className={styles.td}
                                        style={{ width: columnWidths[col] }}
                                    >
                                        {row[col] ?? ""}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>

            {!data.length && (
                <div className={styles.empty}>Нет данных</div>
            )}
        </div>
    );
};

export default UniversalReportTable;

